import { distanceTo, type Bounds, type Vec2 } from '../geom';
import { sceneBounds } from './pick';
import type { Drawn, ResolvedView } from './resolve';

/** The view named `name`, or why there is no one view by that name. */
export function findView(drawn: Drawn, name: string): ResolvedView | string {
  const found = drawn.views.filter((view) => view.name === name);
  if (found.length === 1) return found[0]!;
  if (found.length > 1) return `More than one view is named "${name}"; rename one with .named()`;
  if (drawn.views.length === 0) return `No view "${name}"; the drawing has no views`;
  const names = new Set(drawn.views.map((view) => view.name));
  return `No view "${name}"; the views are ${[...names].join(', ')}`;
}

/**
 * What one view holds, without its own frame and title or any other view, and the bounds of
 * what it holds, without the view's margin, for drawing that view alone.
 */
export function viewContents(
  drawn: Drawn,
  view: ResolvedView,
): { drawn: Drawn; bounds: Bounds | undefined } {
  const members = new Set(view.memberIds);
  const contents: Drawn = {
    entities: drawn.entities.filter((each) => members.has(each.id)),
    dimensions: drawn.dimensions.filter((each) => members.has(each.id)),
    views: [],
  };
  return { drawn: contents, bounds: sceneBounds(contents) ?? undefined };
}

/** The nearest shown view whose frame edge is within the tolerance of `point`. */
export function pickView(drawn: Drawn, point: Vec2, toleranceWorld: number): ResolvedView | null {
  let best: ResolvedView | null = null;
  let bestDistance = toleranceWorld;
  for (const view of drawn.views) {
    if (!view.visible) continue;
    for (const edge of view.layout.strokes) {
      const distance = distanceTo(edge, point);
      if (distance <= bestDistance) {
        best = view;
        bestDistance = distance;
      }
    }
  }
  return best;
}
